/** @typedef {import('../../types/finance').Fee} Fee */
/** @typedef {import('../../types/finance').MerchantFee} MerchantFee */

import { useEffect, useState } from 'react';
import { useQuery } from 'react-query';

import { fetchFees, fetchFeesMerchant, fetchFeeTypes } from '../../api';
import { queryClient } from '../../config';
import { handleQueryError } from '../../utils';

/**
 * @param {string} [type]
 * @returns {{ fees: Fee[], isFetching: boolean, isLoading: boolean, searchQuery: string, setSearchQuery: Function, isRefetching: boolean, setStatus: Function, status: string, refetch: Function }}
 */
const useFetchFees = (type = 'all') => {
  const [fees, setFees] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [status, setStatus] = useState(type);

  const { isLoading, isError, isFetching, isRefetching, data, error, refetch } = useQuery(
    ['allFees', status],
    () => fetchFees(status, searchQuery)
  );

  if (isError) {
    handleQueryError(error, 'fees');
  }

  useEffect(() => {
    if (data && data.data) {
      setFees(data.data);
    }
  }, [data]);

  useEffect(() => {
    queryClient.removeQueries('allFees', { exact: true });
    refetch({ refetchPage: (page, index) => index === 0, resetQueries: true });
  }, [searchQuery, status]);

  return {
    fees,
    isFetching,
    isLoading,
    searchQuery,
    setSearchQuery,
    isRefetching,
    setStatus,
    status,
    refetch
  };
};

/** @returns {{ feeTypes: string[], isFetching: boolean, isLoading: boolean, isRefetching: boolean, refetch: Function }} */
const useFetchFeeTypes = () => {
  const [feeTypes, setFeeTypes] = useState([]);

  const { isLoading, isError, isFetching, isRefetching, data, error, refetch } = useQuery(
    ['allFeeTypes'],
    () => fetchFeeTypes()
  );

  if (isError) {
    handleQueryError(error, 'allFeeTypes');
  }

  useEffect(() => {
    if (data && data.data) {
      setFeeTypes(data.data);
    }
  }, [data]);

  return {
    feeTypes,
    isFetching,
    isLoading,
    isRefetching,
    refetch
  };
};

/**
 * @param {string} type
 * @returns {{ fee: Partial<MerchantFee>, isFetching: boolean, isLoading: boolean, isRefetching: boolean, refetch: Function }}
 */
const useFetchFeeMerchant = (type) => {
  const [fee, setFee] = useState({});

  const { isLoading, isError, isFetching, isRefetching, data, error, refetch } = useQuery(
    ['merchantFee', type],
    () => fetchFeesMerchant(type)
  );

  if (isError) {
    handleQueryError(error, 'merchantFee');
  }

  useEffect(() => {
    if (data && data.data) {
      const fee = data.data;

      const feeObj = {
        value: fee.value,
        value_type: fee.value_type,
        type: fee.type
      };
      setFee(feeObj);
    }
  }, [data]);

  useEffect(() => {
    queryClient.removeQueries('merchantFee');
  }, []);

  return {
    fee,
    isFetching,
    isLoading,
    isRefetching,
    refetch
  };
};

export { useFetchFeeMerchant, useFetchFees, useFetchFeeTypes };
